// src/pages/Users/Lexique/components/LexiqueTermCard.jsx

import React from "react";
import {
  FaCopy,
  FaHeart,
  FaRegHeart,
  FaVolumeUp,
} from "react-icons/fa";

import useLexiqueSpeech from "../hooks/useLexiqueSpeech";

import {
  highlightParts,
  speakWith,
} from "../utils/lexiqueUtils";

export default function LexiqueTermCard({
  row,
  query,
  isFavorite,
  onToggleFavorite,
}) {
  const japonais = row?.japonais || "";
  const traduction = row?.traduction || "";

  const japaneseSpeechText = useLexiqueSpeech(japonais);

  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`${japonais} — ${traduction}`);
    } catch {
      // Non bloquant.
    }
  };

  return (
    <article className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Japonais
          </p>

          <h3 className="mt-1 text-xl font-bold leading-snug text-slate-950 dark:text-white">
            <Highlight text={japonais} query={query} />
          </h3>
        </div>

        <button
          type="button"
          onClick={onToggleFavorite}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition active:scale-95 ${
            isFavorite
              ? "border-red-200 bg-red-50 text-red-500 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300"
              : "border-slate-200 bg-slate-50 text-slate-500 hover:text-red-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"
          }`}
          aria-label={
            isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"
          }
        >
          {isFavorite ? <FaHeart /> : <FaRegHeart />}
        </button>
      </div>

      <div className="mt-4 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
          Traduction
        </p>

        <p className="mt-1 text-sm font-semibold leading-6 text-slate-700 dark:text-slate-300">
          <Highlight text={traduction} query={query} />
        </p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <ActionButton
          onClick={() => speakWith("ja", japaneseSpeechText, "ja-JP")}
          icon={FaVolumeUp}
          label="JP"
          title="Prononcer en japonais"
        />

        <ActionButton
          onClick={() => speakWith("fr", traduction, "fr-FR")}
          icon={FaVolumeUp}
          label="FR"
          title="Prononcer en français"
        />

        <ActionButton
          onClick={copyBoth}
          icon={FaCopy}
          label="Copier"
          title="Copier le terme et sa traduction"
        />
      </div>
    </article>
  );
}

function Highlight({ text, query }) {
  const parts = highlightParts(text, query);

  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          <mark
            key={`${part.text}-${index}`}
            className="rounded bg-yellow-200 px-0.5 text-slate-950 dark:bg-yellow-500/60"
          >
            {part.text}
          </mark>
        ) : (
          <React.Fragment key={`${part.text}-${index}`}>
            {part.text}
          </React.Fragment>
        )
      )}
    </>
  );
}

function ActionButton({ icon: Icon, label, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-2xl bg-slate-100 px-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
    >
      <Icon />
      {label}
    </button>
  );
}